import Phaser from "phaser";
import { GameConfig } from "@/config/GameConfig";
import { classifyGameplayViewport } from "@/utils/ViewportClassification";
import { readViewportSignals } from "@/utils/readViewportSignals";

/**
 * PR31 follow-up fix. The ROTATE YOUR DEVICE / TAP TO CONTINUE portrait-
 * only mobile gate previously lived entirely inside `GameScene` - every
 * other Scene (Main Menu, World/Level Select, every result/leaderboard
 * screen) had no orientation handling at all. On a real device, rotating
 * away from and back to portrait while on one of those screens left
 * Phaser's own per-scene resize/rebuild cycle (each Scene's own
 * `buildLayout()`/`onResize()`, torn down and rebuilt across however many
 * `resize`/`orientationchange` events a real rotation fires) to run
 * completely unsupervised - with nothing deliberately freezing/restoring
 * that Scene's own interactive state across the transition, exactly the
 * kind of "looks fine, but buttons stopped responding" symptom a real
 * device rotation reproduced. This module is the fix: ONE centralized,
 * tested implementation every Scene attaches with two lines, instead of
 * duplicating GameScene's own gate (or inventing a different one) per
 * Scene.
 *
 * Two behaviors, chosen per Scene via `requiresExplicitResume`:
 *  - Default (every non-gameplay Scene): landscape shows the same full-
 *    screen ROTATE YOUR DEVICE overlay and blocks interaction underneath
 *    it (see the overlay's own doc for how); returning to portrait hides
 *    it and restores interaction IMMEDIATELY - no TAP TO CONTINUE, no
 *    armed-tap delay. There is no run to protect and no state to freeze,
 *    so the spec's own requirement ("buttons work immediately") is the
 *    correct, safe default.
 *  - Gameplay (`GameScene`, `requiresExplicitResume: () => true`):
 *    unchanged from the original GameScene-only implementation - landscape
 *    freezes the run (via `onBlockStart`, e.g. clearing the input queue
 *    and pausing feedback/audio) and shows ROTATE YOUR DEVICE; returning
 *    to portrait shows TAP TO CONTINUE and never auto-resumes, armed only
 *    once any pointer already down at that moment has been lifted (so the
 *    tail of the physical rotation/regrip gesture itself can never
 *    register as the resume tap); an explicit tap then resumes via
 *    `onBlockEnd`.
 *
 * Pure per-Scene lifecycle glue only (no shared/global state, no
 * singleton Scene, no cross-Scene input trickery) - each Scene that calls
 * `attachOrientationGate()` gets its OWN overlay Container and OWN
 * `blocked`/armed state, scoped entirely to that Scene the same way
 * GameScene's original implementation was, which is what lets the
 * overlay's own opaque, high-depth, interactive background fully block
 * that Scene's other buttons using Phaser's own default `topOnly` input
 * behavior (the exact mechanism GameScene's Pause/Game-Over/original
 * Rotate overlays already relied on) - no `scene.input.enabled` toggling
 * needed, which would also have disabled the overlay's own tap-to-resume.
 */

export interface OrientationGateOptions {
  /**
   * Re-evaluated every time the gate is about to decide how to handle a
   * landscape -> portrait transition. `true` means "freeze + require an
   * explicit TAP TO CONTINUE" (gameplay); omitted/`false` (the default)
   * means "restore immediately" - correct for every non-gameplay Scene.
   */
  requiresExplicitResume?: () => boolean;
  /** Fires exactly once when a block newly begins (never repeatedly while still blocked) - e.g. GameScene clearing its input queue and pausing feedback/audio. Most Scenes don't need this at all. */
  onBlockStart?: () => void;
  /** Fires exactly once when a block ends - either immediately (non-gameplay) or after an explicit tap (gameplay). */
  onBlockEnd?: () => void;
}

export interface OrientationGateHandle {
  readonly blocked: boolean;
  /** Re-evaluates the live viewport and enters/exits the block accordingly. Call once at the very end of the Scene's own `create()` (after every other pre-gameplay overlay/state is already set up, so it can immediately cover them if the Scene is reached already in landscape) - the resize/orientation-change listener that re-triggers this automatically is already wired internally. */
  check(): void;
  /** Tears down the overlay and internal listeners. Already wired automatically to the Scene's own `shutdown`/`destroy` events - callers don't normally need to call this directly. */
  destroy(): void;
}

/**
 * The `Phaser.GameObjects.GameObject.name` this module's own overlay
 * Container is tagged with - see `destroyLayoutChildren()` below.
 */
const OVERLAY_NAME = "orientation-gate-overlay";

/**
 * A handful of Scenes (`MenuScene`, `EntryScene`, `WorldSelectScene`,
 * `LevelSelectScene`) rebuild their ENTIRE layout on every resize by
 * calling `this.children.removeAll(true)` directly on the Scene's own
 * top-level display list, rather than confining that wipe to a dedicated
 * sub-container the way every other Scene in this codebase does (their
 * own `mainContainer.removeAll(true)`). Since `attachOrientationGate()`'s
 * overlay is itself just another top-level child of that same display
 * list, a bare `removeAll(true)` would destroy it on the very first
 * resize - exactly the kind of "looks fine, but broken underneath" state
 * this module exists to prevent, and worse, would leave its `scene.scale`
 * resize listener still registered and pointing at now-destroyed
 * GameObjects. Rather than rewrite those four Scenes onto the
 * `mainContainer` pattern (a much larger, purely cosmetic-refactor-sized
 * diff for no behavior change), this is a drop-in replacement for their
 * `this.children.removeAll(true)` call that destroys every top-level
 * child EXCEPT the orientation gate's own overlay, leaving it - and only
 * it - to survive the rebuild exactly like `mainContainer.removeAll(true)`
 * already does for it in the other Scenes.
 */
export function destroyLayoutChildren(scene: Phaser.Scene): void {
  for (const child of [...scene.children.list]) {
    if (child.name === OVERLAY_NAME) continue;
    child.destroy();
  }
}

/**
 * Attaches a full ROTATE YOUR DEVICE / TAP TO CONTINUE gate to `scene`.
 * Internally wires its own `scene.scale.on("resize", ...)` listener and
 * its own `scene.events.once("shutdown"/"destroy", ...)` cleanup, so a
 * caller only ever needs to call the returned handle's `check()` once, at
 * the end of its own `create()` - see that method's own doc.
 */
export function attachOrientationGate(scene: Phaser.Scene, options: OrientationGateOptions = {}): OrientationGateHandle {
  let blocked = false;
  let tapToContinueArmed = false;

  const overlay = scene.add.container(0, 0);
  overlay.setName(OVERLAY_NAME); // See `destroyLayoutChildren()`'s own doc above.
  overlay.setDepth(1000); // Higher than every existing per-Scene overlay depth in this codebase (GameScene's own highest is 100) - always wins regardless of which Scene this is attached to.
  overlay.setVisible(false);

  const bg = scene.add.rectangle(scene.scale.width / 2, scene.scale.height / 2, scene.scale.width, scene.scale.height, GameConfig.colors.background, 1);
  bg.setInteractive({ useHandCursor: false });
  bg.on("pointerdown", () => handleTapToResume());
  overlay.add(bg);

  const panel = scene.add.container(scene.scale.width / 2, scene.scale.height / 2);
  overlay.add(panel);

  const iconW = 34;
  const iconH = 58;
  const icon = scene.add.graphics();
  icon.lineStyle(3, 0xf5f7fa, 0.9);
  icon.strokeRoundedRect(-iconW / 2, -iconH / 2, iconW, iconH, 8);
  icon.fillStyle(0xf5f7fa, 0.9);
  icon.fillCircle(0, iconH / 2 - 9, 2.5);
  icon.setPosition(0, -60);

  const iconTween = scene.tweens.add({
    targets: icon,
    angle: -90,
    duration: 700,
    hold: 500,
    yoyo: true,
    repeat: -1,
    ease: "Sine.easeInOut",
  });

  const text = scene.add
    .text(0, 10, "ROTATE YOUR DEVICE", {
      fontFamily: "Arial, sans-serif",
      fontSize: "22px",
      fontStyle: "bold",
      color: "#f5f7fa",
      align: "center",
    })
    .setOrigin(0.5);

  panel.add([icon, text]);

  function setMode(mode: "rotate" | "resume"): void {
    if (mode === "rotate") {
      text.setText("ROTATE YOUR DEVICE");
      icon.setAngle(0);
      iconTween.resume();
    } else {
      text.setText("TAP TO CONTINUE");
      iconTween.pause();
      icon.setAngle(-90);
    }
  }

  function enterBlock(): void {
    blocked = true;
    tapToContinueArmed = false;
    setMode("rotate");
    overlay.setVisible(true);
    overlay.setAlpha(1);
    options.onBlockStart?.();
  }

  /** Same deferred-arm technique the original GameScene-only implementation used - see this module's own top-of-file doc. */
  function armTapToContinue(): void {
    if (scene.input.activePointer.isDown) {
      scene.input.once("pointerup", () => {
        if (blocked && classifyGameplayViewport(readViewportSignals()) !== "mobile-landscape") {
          tapToContinueArmed = true;
        }
      });
      return;
    }
    tapToContinueArmed = true;
  }

  function resume(): void {
    blocked = false;
    tapToContinueArmed = false;
    overlay.setVisible(false);
    options.onBlockEnd?.();
  }

  function handleTapToResume(): void {
    if (!tapToContinueArmed) return;
    resume();
  }

  function check(): void {
    const isLandscapePhone = classifyGameplayViewport(readViewportSignals()) === "mobile-landscape";

    if (isLandscapePhone) {
      if (!blocked) enterBlock();
      return;
    }

    if (!blocked || tapToContinueArmed) return;

    if (options.requiresExplicitResume?.()) {
      // Portrait again after having been blocked - never auto-resume (the
      // player may still be mid-regrip); show TAP TO CONTINUE and require
      // an explicit tap.
      setMode("resume");
      armTapToContinue();
    } else {
      // No run/state to protect - restore immediately.
      resume();
    }
  }

  function resizeOverlay(): void {
    bg.setSize(scene.scale.width, scene.scale.height);
    bg.setPosition(scene.scale.width / 2, scene.scale.height / 2);
    panel.setPosition(scene.scale.width / 2, scene.scale.height / 2);
  }

  function destroy(): void {
    scene.scale.off("resize", onResize);
    scene.tweens.killTweensOf(icon);
    overlay.destroy();
  }

  function onResize(): void {
    resizeOverlay();
    check();
  }

  scene.scale.on("resize", onResize);
  scene.events.once("shutdown", destroy);
  scene.events.once("destroy", destroy);

  return {
    get blocked() {
      return blocked;
    },
    check,
    destroy,
  };
}
